function format(amount, decimalSeparator = ',', fixed = true, fixedDigits = 2): string {
    const value = Number(amount);

    if (Number.isNaN(value) || amount === null) {
        return '';
    }

    const startingPosition = value > 0 ? 0 : 1;
    const multiplierValue = 10 ** fixedDigits;
    // We use the multiplier to mitigate rounding errors due floating point precision loss with ToFixed
    // See more info here - https://stackoverflow.com/questions/11695618/dealing-with-float-precision-in-javascript
    const fixedValue = fixed ? (Math.round(value * multiplierValue) / multiplierValue).toFixed(fixedDigits) : value;
    const indexOfDecimalSeparator = String(fixedValue).indexOf('.');
    if (decimalSeparator === ',') {
        return String(fixedValue).replace(/./g, (c, i, a) => {
            const lengthOfDecimalPartAndSeparator = fixedDigits + 1;
            if (c === '.') {
                return ',';
            }
            if (
                i > startingPosition &&
                i < indexOfDecimalSeparator &&
                c !== '.' &&
                (a.length - i - lengthOfDecimalPartAndSeparator) % 3 === 0 // Subtracting number of fixed digits - fixed digits are not needed to be counted in case when applying thousand separator
            ) {
                return `.${c}`;
            }

            return c;
        });
    }

    return String(fixedValue).replace(/./g, (c, i, a) => {
        return i > startingPosition && i < indexOfDecimalSeparator && c !== '.' && (a.length - i) % 3 === 0 ? `,${c}` : c;
    });
}

function parse(amount = '', decimalSeparator = ','): number | undefined {
    if (typeof amount === 'number') {
        return amount;
    }

    if (!amount?.length) {
        return undefined;
    }

    let value;

    if (decimalSeparator === ',') {
        // replace digit seperator then replace decimal separator
        value = Number(amount.replace(/\./g, '').replace(',', '.'));
    } else {
        // replace digit seperator
        value = Number(amount.replace(/,/g, ''));
    }

    return Number.isNaN(value) ? undefined : value;
}

function round(value, decimalPlaces = 2): number {
    // use EPSILON to offset floating-point inaccuracies
    const adjustedValue = value * (1 + Number.EPSILON);

    return parseFloat(adjustedValue.toFixed(decimalPlaces));
}

function parseExpression(textValue, decimalSeparator, fixedDigits = 2): string {
    try {
        if (typeof textValue === 'number') {
            const valueNumberFormatted = format(textValue, decimalSeparator, true, fixedDigits);
            return valueNumberFormatted;
        }

        const textValueString = String(textValue);
        if (textValueString.trim() === '') {
            return format(textValue, decimalSeparator, true, fixedDigits);
        }

        const parsedExprToNr = parse(textValueString, decimalSeparator);
        if (parsedExprToNr !== undefined) {
            const formatedValid = format(parsedExprToNr, decimalSeparator, true, fixedDigits);
            return formatedValid;
        }

        if (
            textValue === undefined ||
            (!textValueString.trim().startsWith('+', 0) &&
                !textValueString.trim().startsWith('-', 0) &&
                !textValueString.trim().startsWith('=', 0))
        ) {
            // An expression is considered a number if it starts with "=, +, or -" as in Daybook
            const parsedInvalidExpression = format(undefined, decimalSeparator, true, fixedDigits);
            return parsedInvalidExpression;
        }

        let expressionStr = textValueString;
        if (textValueString.trim().startsWith('=', 0)) {
            expressionStr = textValueString.trim().substring(1);
        }

        const regExpContent = '^[^a-zA-Z$%&@!~#?`^]*[+-/*]*([(]*)*([0-9.]?([0-9,]?)([)]*))+$';

        const regExpExpression = new RegExp(regExpContent);
        const matchesRegExp = regExpExpression.test(expressionStr.replace(/\s/g, ''));

        if (!matchesRegExp) {
            const parsedUnmatchedRegExp = format(undefined, decimalSeparator, true, fixedDigits);
            return parsedUnmatchedRegExp;
        }

        const expressionMatchArray =
            expressionStr.replace(/\s/g, '').match(/([$%&@!~#?`]+)*([(])*[+-/*]*([(]+?)*([0-9.]?([0-9,]?[0-9.]+?)+=*([)])?)/g) ||
            [];

        const clonedExpressionTerms = JSON.parse(JSON.stringify(expressionMatchArray));

        clonedExpressionTerms.forEach((element, index) => {
            const numberOnly = element.match(/([0-9,]*[0-9.]+)*([0-9.]*[0-9,]+)/g) || [];
            if (numberOnly !== undefined && numberOnly !== '') {
                const numberParsed = parse(numberOnly.toString(), decimalSeparator);
                if (numberParsed !== undefined) {
                    clonedExpressionTerms[index] = element.replace(numberOnly.toString(), numberParsed.toString());
                }
            }
        });

        const expressionJoined = clonedExpressionTerms.join('');
        if (expressionJoined.includes(',')) {
            const parsedDefaultExpr = format(undefined, decimalSeparator, true, fixedDigits);
            return parsedDefaultExpr;
        }

        // eslint-disable-next-line
        let resultEval = Function(`'use strict'; return (${expressionJoined})`)();
        const parsedNumberFormat = format(resultEval, decimalSeparator, true, fixedDigits);
        return parsedNumberFormat;
    } catch (error) {
        const parsedDefault = format(undefined, decimalSeparator, true, fixedDigits);
        return parsedDefault;
    }
}

export default {
    round,
    format,
    parse,
    parseExpression,
};
